import {
  Badge,
  Button,
  InlineStack,
  Link,
  Modal,
  BlockStack,
} from '@shopify/polaris';
import {useI18n} from '@shopify/react-i18n';
import {ContentSwitcher} from 'components/ContentSwitcher';
import {useMonorail} from 'packages/monorail';
import {useSessionId} from '@web-utilities/session-id';
import {useState} from 'react';
import {useToast} from 'utilities/toasts';
import {ShopLocale} from 'utilities/use-shop-locales-and-markets';

import {DisclaimerModal} from '../DisclaimerModal';

export interface ModalProps {
  open: boolean;
  resourceName: string;
  shopLocale: ShopLocale;
  wholeShop: boolean;
  machineTranslatedLocales: ShopLocale[];
  machineTranslationLocaleLimit: number;
  machineTranslationCustomCredentialsEnabled: boolean;
  onCancel(): void;
  onConfirm(): void;
}

export function ConfirmMachineTranslateModal({
  open,
  resourceName,
  shopLocale,
  wholeShop,
  machineTranslatedLocales,
  machineTranslationLocaleLimit,
  machineTranslationCustomCredentialsEnabled,
  onConfirm,
  onCancel,
}: ModalProps) {
  const [disclaimerModalOpen, setDisclaimerModalOpen] = useState(false);
  const [i18n] = useI18n();
  const monorail = useMonorail();
  const sessionId = useSessionId();
  const toast = useToast();

  const appName = i18n.translate('Common.appName');
  const localeName = shopLocale.name;

  const machineTranslatedLocalesCount = machineTranslatedLocales.length;
  const firstTimeMachineTranslating = machineTranslatedLocalesCount === 0;
  const hasExceededLimit =
    machineTranslatedLocalesCount >= machineTranslationLocaleLimit;
  const localeAlreadyMachineTranslated = shopLocale.machineTranslated;

  const primaryAction =
    !machineTranslationCustomCredentialsEnabled &&
    hasExceededLimit &&
    !localeAlreadyMachineTranslated
      ? undefined
      : {
          content: i18n.translate(
            'ConfirmMachineTranslateModal.actionTranslate',
          ),
          onAction: () => onConfirm(),
        };

  const secondaryActions =
    !machineTranslationCustomCredentialsEnabled &&
    hasExceededLimit &&
    !localeAlreadyMachineTranslated
      ? undefined
      : [
          {
            content: i18n.translate('Common.cancel'),
            onAction: () => onCancel(),
          },
        ];

  const signUpAction = () => {
    monorail.produce({
      schemaId: 'machine_translations_language_limit_mailing_list_signup/1.0',
      payload: {
        sessionToken: sessionId,
      },
    });
    onCancel();
    toast.show({
      content: i18n.translate('ConfirmMachineTranslateModal.signUpSuccessful'),
    });
  };

  const storeTranslateUsingGoogle = wholeShop && (
    <p>
      {i18n.translate(
        'ConfirmMachineTranslateModal.storeTranslateUsingGoogle',
        {
          poweredByGoogleLink: (
            <Link
              removeUnderline
              target="_blank"
              url="https://cloud.google.com/translate"
            >
              {i18n.translate(
                'ConfirmMachineTranslateModal.poweredByGoogleLinkLabel',
              )}
            </Link>
          ),
        },
      )}
    </p>
  );

  return (
    <>
      <Modal
        open={open && !disclaimerModalOpen}
        instant
        title={i18n.translate('ConfirmMachineTranslateModal.title', {
          resourceName,
        })}
        onClose={() => onCancel()}
        primaryAction={primaryAction}
        secondaryActions={secondaryActions}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <ContentSwitcher>
              <ContentSwitcher.When condition={firstTimeMachineTranslating}>
                {storeTranslateUsingGoogle}
                <p>
                  {machineTranslationCustomCredentialsEnabled
                    ? i18n.translate(
                        'ConfirmMachineTranslateModal.aboutToTranslateMessage',
                        {
                          localeName,
                        },
                      )
                    : i18n.translate(
                        'ConfirmMachineTranslateModal.addingFirstLanguage',
                        {
                          localeName,
                        },
                      )}
                </p>
              </ContentSwitcher.When>
              <ContentSwitcher.When
                condition={
                  machineTranslationCustomCredentialsEnabled ||
                  machineTranslatedLocalesCount === 1 ||
                  localeAlreadyMachineTranslated
                }
              >
                <InlineStack gap="200">
                  {machineTranslatedLocales.map((locale) => (
                    <Badge
                      tone="success"
                      key={`badge-${locale.locale}`}
                      progress="complete"
                    >
                      {i18n.translate(
                        'ConfirmMachineTranslateModal.localeAutoTranslated',
                        {
                          localeName: locale.name,
                        },
                      )}
                    </Badge>
                  ))}
                  {!machineTranslationCustomCredentialsEnabled &&
                    machineTranslatedLocalesCount <
                      machineTranslationLocaleLimit && (
                      <Badge progress="incomplete">
                        {i18n.translate(
                          'ConfirmMachineTranslateModal.autoTranslationRemaining',
                        )}
                      </Badge>
                    )}
                </InlineStack>
                {storeTranslateUsingGoogle}
                {!localeAlreadyMachineTranslated && (
                  <p>
                    {machineTranslationCustomCredentialsEnabled
                      ? i18n.translate(
                          'ConfirmMachineTranslateModal.aboutToTranslateMessage',
                          {
                            localeName,
                          },
                        )
                      : i18n.translate(
                          'ConfirmMachineTranslateModal.addingSecondLanguage',
                          {
                            localeName,
                          },
                        )}
                  </p>
                )}
              </ContentSwitcher.When>
              <ContentSwitcher.Fallback>
                <InlineStack gap="200">
                  {machineTranslatedLocales.map((locale) => (
                    <Badge
                      tone="success"
                      key={`badge-${locale.locale}`}
                      progress="complete"
                    >
                      {i18n.translate(
                        'ConfirmMachineTranslateModal.localeAutoTranslated',
                        {
                          localeName: locale.name,
                        },
                      )}
                    </Badge>
                  ))}
                </InlineStack>
                <p>
                  {i18n.translate(
                    'ConfirmMachineTranslateModal.addingThirdLanguage',
                    {
                      localeName,
                      appName,
                    },
                  )}
                </p>
                <p>
                  {i18n.translate(
                    'ConfirmMachineTranslateModal.twoLanguagesLimitNotice',
                    {
                      signUpButton: (
                        <Button
                          variant="plain"
                          external
                          onClick={() => signUpAction()}
                        >
                          {i18n.translate(
                            'ConfirmMachineTranslateModal.signUp',
                          )}
                        </Button>
                      ),
                    },
                  )}
                </p>
                <p>
                  {i18n.translate('ConfirmMachineTranslateModal.thankYou', {
                    appName,
                  })}
                </p>
              </ContentSwitcher.Fallback>
            </ContentSwitcher>
            {(machineTranslationCustomCredentialsEnabled ||
              !hasExceededLimit ||
              localeAlreadyMachineTranslated) && (
              <ContentSwitcher>
                <ContentSwitcher.When condition={wholeShop}>
                  <p>
                    {i18n.translate(
                      'ConfirmMachineTranslateModal.translationDetailsForWholeShop',
                      {
                        localeName,
                      },
                    )}
                  </p>
                </ContentSwitcher.When>
                <ContentSwitcher.Fallback>
                  <p>
                    {i18n.translate(
                      'ConfirmMachineTranslateModal.translationDetailsForSpecificResource',
                      {
                        localeName,
                      },
                    )}
                  </p>
                </ContentSwitcher.Fallback>
              </ContentSwitcher>
            )}
          </BlockStack>
        </Modal.Section>
        {firstTimeMachineTranslating && (
          <Modal.Section>
            <small>
              {i18n.translate('ConfirmMachineTranslateModal.legalNotice', {
                appName,
                disclaimerLink: (
                  <Link
                    removeUnderline
                    onClick={() => setDisclaimerModalOpen(true)}
                  >
                    {i18n.translate(
                      'ConfirmMachineTranslateModal.disclaimerLinkLabel',
                    )}
                  </Link>
                ),
              })}
            </small>
          </Modal.Section>
        )}
      </Modal>
      <DisclaimerModal
        open={disclaimerModalOpen}
        resourceName={resourceName}
        onClose={() => setDisclaimerModalOpen(false)}
      />
    </>
  );
}
